// Pixi viewport — replaces the original Canvas2D renderer.
//
// Why Pixi: Canvas2D rasterizes every shape per-frame on the CPU, which
// re-anti-aliases edges every time a shape moves sub-pixel. That causes
// visible shimmer when the camera smoothly scrolls past walls and other
// shapes — the rendering math is correct but the per-frame anti-alias
// computation gives slightly different pixel coverage frame to frame.
// Pixi's WebGL/WebGPU backend GPU-rasterizes once-per-frame with
// texture-style sampling on layer transforms, so smooth sub-pixel
// motion produces visually smooth output.
//
// Architecture:
//   - One Pixi Application that owns the existing HTMLCanvasElement.
//   - A `worldContainer` scaled by (ppwu, -ppwu) (y-flip + zoom) and
//     translated by (-camera*ppwu + screen_center) every frame. Children
//     of this container draw in WORLD units: a wall cell is just
//     `rect(wx, wy, 1, 1)`, a player is `circle(wx, wy, 0.7)`.
//   - A `hudContainer` at identity transform for HP bars, lag text,
//     and the disconnected placeholder — these have to stay
//     screen-pixel-sized regardless of zoom.
//   - Each rendering layer (grid, origin, walls, entities, build
//     overlay, hp bars) is one Graphics object that's cleared and
//     rebuilt every frame. One-shot stuff (text) gets a Text node.
//
// Public API is unchanged: `setSnapshot`, the four resolvers,
// `cursorWorldFromMouse`, `aimAngleFromMouse`, `setConnected`,
// `setDisconnected`, `start`, `stop`, `getFps`. `main.ts` doesn't have
// to know we switched renderers.
//
// Two sources of position data per frame:
//
//   1. Self entity (id == `yourId` from the latest snapshot) is drawn at
//      the *predicted* position from `PredictionEngine` — no interp.
//   2. Every other entity is drawn lerped between snapshots via the
//      tick-anchored `SnapshotBuffer` clock model.

import {
  Application,
  Container,
  Graphics,
  Text,
  type Ticker,
} from "pixi.js";

import type { SnapshotMsg } from "../worker-protocol";
import {
  makeCamera,
  screenToWorld,
  visibleWorldRect,
  worldToScreen,
  type Camera,
  type ViewportSize,
  type WorldRect,
} from "./camera";

/** Background fill behind everything else. Pixi uses this as the
 *  renderer's clear color so we don't have to draw a full-screen rect
 *  each frame; the GPU compositor handles it on the prior frame's
 *  buffer swap. The grid, origin, walls, etc. each get their own
 *  CSS-color constants inline at the draw site. */
const BG_COLOR = "#0e0e10";

// Tick-based snapshot interpolation tunables. The buffer holds the
// last few snapshots and we render at `latestServerTick - DELAY_TICKS`,
// finding the two snapshots that straddle that point and lerping by
// tick distance (NOT wall-clock time — server snapshot cadence is
// uneven 33/67 ms, but tick deltas are exact).
//
// 3 ticks ≈ 100 ms of render lag for remote entities. Enough buffer to
// absorb network jitter without overshooting. The same number the brief
// originally targeted for the snapshot interpolation step.
const SNAPSHOT_DELAY_TICKS = 3;
/** Server's physics rate. Used to convert wall-clock elapsed time into
 *  projected server ticks since the latest snapshot arrived. Mirrors
 *  the server's `TICK_DURATION` (33.333… ms). */
const SERVER_TICK_RATE_MS = 1000 / 30;
/** Ring-buffer depth. 6 frames = ~300 ms of history at the average
 *  snapshot cadence. Plenty of slack for jitter, cheap on memory. */
const SNAPSHOT_BUFFER_SIZE = 6;
// Wall fills + strokes per kind. The two color pairs let the player
// read player-placed walls (destructible) vs procedural barriers
// (indestructible) at a glance. Fills are SOLID (alpha = 1) so any
// sub-pixel anti-aliasing at cell boundaries can't let the dark
// background bleed through as a flickery seam between adjacent cells.
// Outline alpha is fine since outlines paint over the solid fill, not
// over background-adjacent areas.
//
// Numeric values mirror `shared::protocol::WallKind` exactly so we can
// index by the kind byte coming off the wire.
const WALL_KIND_PLAYER = 0;
const WALL_KIND_BARRIER = 1;
const WALL_FILL_PLAYER = "#aaafc8";
const WALL_FILL_BARRIER = "#404250";
/** Outline drawn on every edge that faces a non-wall OR a different
 *  kind of wall. Two different stroke tones so the outline reads well
 *  against its own kind's fill — dark outline on the light player
 *  wall, light outline on the dark barrier. Identical at internal
 *  edges between two same-kind cells (which are skipped entirely),
 *  so this only matters on the silhouette of each connected group.
 *
 *  Alpha is 1.0 — the outline doubles as the seam covering any
 *  sub-pixel anti-aliasing artifact at a player↔barrier boundary,
 *  where two separate fill paths can otherwise leak the dark
 *  background through a 1-px-wide seam. With opaque strokes the
 *  boundary is always rendered as a clean line. */
const WALL_STROKE_PLAYER = "rgb(28, 32, 46)";
const WALL_STROKE_BARRIER = "rgb(180, 185, 210)";
/** Predicted walls (client-side prediction additions) are always
 *  player-placed by construction — only the build system creates them. */
const PREDICTED_WALL_KIND = WALL_KIND_PLAYER;

// ---------------------------------------------------------------------------
// Border / line widths, in SCREEN pixels. Every stroke below multiplies one
// of these by `px = 1 / ppwu` (one screen pixel in world units) so the
// on-screen width is constant regardless of zoom.
//
// Why thicker than 1 px: a 1-device-pixel GPU-antialiased line has its
// coverage spread across two physical pixels, and as the camera scrolls
// sub-pixel (worst on diagonal motion, where BOTH axes move fractionally)
// that coverage oscillates frame-to-frame — the lines visibly crawl/flicker.
// A ≥2 px line always fully covers at least one pixel, so the shimmer
// collapses to a barely-visible edge. Bump these if flicker persists.
// ---------------------------------------------------------------------------
const GRID_MINOR_PX = 1.5;
const GRID_MAJOR_PX = 2;
const ORIGIN_PX = 2;
const WALL_OUTLINE_PX = 2.5;

/** Wall fills are drawn slightly oversized — each cell rect grows by this
 *  many screen pixels on every side — so adjacent same-kind cells OVERLAP
 *  instead of merely abutting. Abutting rects in one fill leave a sub-pixel
 *  anti-aliasing seam between them (each rect is AA'd independently), which
 *  reads as a faint grid over a wall group — i.e. the cells look "not
 *  merged". The internal-edge outlines are intentionally skipped (only the
 *  silhouette is stroked), so nothing else covers that seam. A half-pixel
 *  bleed makes neighbors overlap by ~1 px and the seam disappears; since a
 *  group is one solid color, the overlap is invisible. */
const WALL_FILL_BLEED_PX = 0.75;
/** Cell-outline color for the place-preview box. Solid white reads
 *  cleanly against both the dark background and any wall fill, and
 *  drops the green/red range signal — the faint build-radius ring
 *  drawn underneath already tells the player whether the cursor is
 *  reachable. Line-width still differs so an in-range cell looks
 *  "selectable" rather than "out of reach". */
const BUILD_OUTLINE_COLOR = "rgba(255, 255, 255, 0.95)";
const BUILD_OUTLINE_OUT_OF_RANGE_COLOR = "rgba(255, 255, 255, 0.35)";
const BREAK_FILL = "rgba(220, 60, 60, 0.18)";
const BREAK_STROKE = "rgba(220, 60, 60, 0.7)";

/** Player AABB half-extent matches the server's `PLAYER_HALF` of 0.7
 *  world units (player is 1.4 cells wide). We draw a circle inscribed
 *  in that box. Keep in sync with server's PLAYER_HALF.x. */
const PLAYER_RADIUS_WORLD = 0.7;

/** Diagnostic hitbox rings drawn over every player. Match the server's
 *  `HIT_RADIUS` (1.1 × visible radius) and `CRIT_RADIUS` (0.5 × visible
 *  radius). Toggle off when balancing feels right — these are temporary
 *  visualizations of the actual damage geometry. */
const HIT_RADIUS_WORLD = PLAYER_RADIUS_WORLD * 1.1; // 0.770
const CRIT_RADIUS_WORLD = PLAYER_RADIUS_WORLD * 0.5; // 0.350
/** Draw the diagnostic hit / crit rings? Flip to `false` once the
 *  hitbox tuning lands. */
const DRAW_HITBOX_OVERLAYS = true;
const HIT_RING_COLOR = "rgba(255, 200, 80, 0.55)"; // amber
const CRIT_RING_COLOR = "rgba(255, 80, 80, 0.85)"; // red bullseye

/** Shared empty-overlay singletons. `drawWalls` falls back to these
 *  every frame when no `BuildPrediction` is wired, so we don't allocate
 *  a new empty `Set` / `Array` per RAF. */
const EMPTY_REMOVALS: ReadonlySet<number> = new Set();
const EMPTY_ADDITIONS: ReadonlyArray<readonly [number, number]> = [];

/** Build / break tunables — mirror the server constants in
 *  `server::game::systems`. Kept in sync by hand for now; when more
 *  shared sim numbers pile up they'll live in a wasm-exported table. */
const BUILD_RADIUS_WORLD = 5;
const BREAK_AOE_RADIUS_WORLD = 0.8;

export interface ConnectionStatus {
  connected: boolean;
  /** Sticky last reason; rendered when `connected` is false. */
  lastReason: string;
}

/** Read-only view over a position the prediction engine controls.
 *  Resolver-style so the viewport can pull on each frame without an
 *  imperative push from the input pump. */
export type SelfPositionResolver = () => { x: number; y: number } | null;

/** Read-only view over the player's live aim angle, in radians, world
 *  y-up. Pulled every render frame so the self aim ray follows the
 *  mouse with zero lag — the snapshot's echoed `facings` value is
 *  20 Hz and would otherwise stair-step. */
export type SelfAimResolver = () => number | null;

/** Optional debug string appended to the HUD line each frame. Wired by
 *  main.ts to surface prediction/reconcile internals on screen. Returns
 *  `null` to append nothing. */
export type DebugResolver = () => string | null;

/** Predicted wall overlay pulled from `BuildPrediction` each frame.
 *  `additions` are cells the renderer should DRAW even though the
 *  server's wall list doesn't include them yet; `removals` are cells
 *  the renderer should HIDE from the server's wall list. Predicted
 *  walls draw with the same fill + outline style as confirmed walls
 *  so placement looks instant. */
export interface BuildOverlay {
  additions: ReadonlyArray<readonly [number, number]>;
  removals: ReadonlySet<number>;
}
export type BuildOverlayResolver = () => BuildOverlay | null;

/** Used by the input pump to translate the current canvas-relative
 *  mouse position into world coordinates. The viewport owns the camera
 *  so it's the natural home for this conversion. */
export type CursorResolver = (
  canvasX: number,
  canvasY: number,
) => { x: number; y: number };

/** Per-frame build-mode state. Pulled via `BuildCursorResolver` every
 *  RAF tick so the placement preview tracks the mouse with no perceived
 *  lag — when this was a 10 Hz push from the HUD interval the cell
 *  outline visibly stuttered behind cursor motion. */
export interface BuildCursorState {
  /** True iff the user is in build mode. When false the renderer skips
   *  the overlay entirely. */
  active: boolean;
  /** Canvas-CSS coordinates, relative to the canvas top-left. Same
   *  units `InputPump` exposes via `peek().mouseX/mouseY`. */
  mouseCanvasX: number;
  mouseCanvasY: number;
  /** True while RMB is held — drives the break-circle render and
   *  suppresses the placement box (you're deleting, not placing). */
  breakHeld: boolean;
}
/** Pull function for the cursor state. Returns `null` before the input
 *  pump is wired (briefly during boot). */
export type BuildCursorResolver = () => BuildCursorState | null;

/** Cached parallel-array snapshot plus a small id→index map so we can
 *  match entities across `prev` / `curr` in O(1) at draw time. */
interface SnapshotFrame {
  tick: number;
  yourId: number;
  arrivedAt: number;
  ids: Uint16Array;
  xs: Float32Array;
  ys: Float32Array;
  facings: Float32Array;
  hps: Uint8Array;
  /** Map from entity id → index into the parallel arrays. */
  indexById: Map<number, number>;
  wallXs: Int32Array;
  wallYs: Int32Array;
  /** Per-wall kind tag (0 = player, 1 = barrier). Parallel to
   *  `wallXs` / `wallYs` — the renderer feeds it into the outline-edge
   *  check so adjacent same-kind walls merge into one shape, but a
   *  player wall butted up against a barrier still gets the seam
   *  drawn. */
  wallKinds: Uint8Array;
  /** Packed `(x,y) → kind` map. Doubles as the "is this cell a wall?"
   *  lookup `drawWalls` does for each neighbor (Map.has is O(1)) while
   *  also serving the kind comparison for the outline-merge decision.
   *  Built once when the snapshot lands. */
  wallKindByKey: Map<number, number>;
}

/** Pack a signed 32-bit cell coord pair into one 32-bit number for
 *  Set membership lookups. The mask + shift keeps the encoding
 *  collision-free for any |coord| ≤ 32_767 (well inside our 1000-cell
 *  world bounds). */
function packCellKey(x: number, y: number): number {
  return ((x & 0xffff) << 16) | (y & 0xffff);
}

/** Shortest-arc angle lerp. Handles the ±π wrap so a 175° → -175°
 *  rotation goes the short way (10° across the boundary) instead of
 *  the long way (350° back through zero). */
function lerpAngle(a: number, b: number, t: number): number {
  let delta = b - a;
  if (delta > Math.PI) delta -= 2 * Math.PI;
  else if (delta < -Math.PI) delta += 2 * Math.PI;
  return a + delta * t;
}

/** Output struct for `SnapshotBuffer.sample()`. Reused across frames
 *  via the buffer's internal scratch field so we don't allocate per
 *  entity per frame. */
interface InterpolatedEntity {
  /** True if the entity is present in the latest snapshot. False when
   *  it has left AOI or disconnected — caller should skip rendering. */
  present: boolean;
  x: number;
  y: number;
  facing: number;
  hp: number;
}

/** Tick-based interpolation buffer for non-self entities.
 *
 *  Replaces the original prev/curr-pair wall-clock interpolation. The
 *  old approach assumed snapshot intervals were a uniform 50 ms, but
 *  the server actually emits at the 20-of-every-30-ticks cadence which
 *  alternates 33 ms / 67 ms. That mismatch was the dominant source of
 *  remote-entity jitter even on a perfect network.
 *
 *  How it works:
 *  - Each new snapshot lands via `push(frame)` and goes onto a small
 *    ring (latest at the end). Old frames roll off.
 *  - Every render frame, `sample(entityId, now)` projects "where the
 *    server is RIGHT NOW" in tick-space, subtracts `SNAPSHOT_DELAY_TICKS`
 *    to put us comfortably in the past, then finds the two frames that
 *    straddle that render tick and lerps positions + facing by tick
 *    distance.
 *  - HP is taken from the LATEST frame regardless — a discrete u8 isn't
 *    something you want to interpolate.
 *
 *  Clock model: a single low-pass-filtered anchor `(anchorTick,
 *  anchorWallTime)` tracks the server's tick rate. Render tick is
 *  always `anchorTick + (now - anchorWallTime) / TICK_MS - DELAY`.
 *  Network jitter doesn't make the render tick jump backward / forward
 *  every time a snapshot arrives — instead, each arrival nudges the
 *  anchor by `CLOCK_SMOOTHING × jitter`, which the next ~10 snapshots
 *  smooth out. This kills the visible per-snapshot stutter remote
 *  entities had even on a stable network.
 */
class SnapshotBuffer {
  private readonly frames: SnapshotFrame[] = [];

  /** Anchor for the smoothed server-tick clock. `anchorTick` is our
   *  estimate of the server tick at wall-clock time `anchorWallTime`.
   *  Every render frame projects forward from this anchor at the
   *  server's real-time rate. Every snapshot arrival low-pass-filters
   *  the anchor toward what that snapshot's tick implies, absorbing
   *  network jitter as a slow drift correction instead of a per-frame
   *  jump. */
  private anchorTick = 0;
  private anchorWallTime = 0;
  private clockInitialized = false;

  /** Low-pass filter coefficient for the anchor drift correction. With
   *  α = 0.1, a single jitter spike has ~37 % of its impact remaining
   *  after 10 snapshots (~500 ms at 20 Hz) — fast enough to converge
   *  on a real clock-rate shift, slow enough that ±10 ms of one-shot
   *  network jitter becomes <0.03 ticks (≈1 ms) of render-tick wobble.
   *  Larger α tracks faster but lets more jitter through; smaller α
   *  smooths harder but accumulates drift on real clock changes. */
  private static readonly CLOCK_SMOOTHING = 0.1;

  /** If a snapshot disagrees with our anchor by more than this many
   *  ticks, hard-snap the anchor instead of smoothing. Catches server
   *  restarts, tab-resumed-from-throttling, and any other discontinuity
   *  that the low-pass filter can't reasonably absorb. */
  private static readonly CLOCK_SNAP_THRESHOLD_TICKS = 10;

  /** Push the latest snapshot. Drops the oldest if we'd exceed the
   *  buffer cap. Assumes monotonic tick numbers in arrival order;
   *  the server's `emit_snapshots` guarantees that.
   *
   *  Each push also updates the smoothed clock anchor:
   *  - First snapshot: anchor at this snapshot's tick + now. From here
   *    on, `renderTick` advances at the real-time rate.
   *  - Subsequent snapshots: re-anchor at `now`, with `anchorTick`
   *    nudged by `CLOCK_SMOOTHING × (observed − projected)`. Big
   *    discontinuities (>CLOCK_SNAP_THRESHOLD_TICKS) hard-snap. */
  push(frame: SnapshotFrame): void {
    const wallNow = frame.arrivedAt; // = performance.now() at receive
    if (!this.clockInitialized) {
      this.anchorTick = frame.tick;
      this.anchorWallTime = wallNow;
      this.clockInitialized = true;
    } else {
      // Where the smoothed clock thinks the server is RIGHT NOW.
      const projected =
        this.anchorTick + (wallNow - this.anchorWallTime) / SERVER_TICK_RATE_MS;
      const diff = frame.tick - projected;
      if (Math.abs(diff) > SnapshotBuffer.CLOCK_SNAP_THRESHOLD_TICKS) {
        // Server restart, long browser throttle, or some other gap the
        // filter can't reasonably absorb. Snap the anchor to the
        // observed tick — better an instant correction than a long
        // visible drift.
        this.anchorTick = frame.tick;
      } else {
        // Re-anchor at `now`, with a partial pull toward the observed
        // tick. The next render frame reads the new anchor.
        this.anchorTick = projected + diff * SnapshotBuffer.CLOCK_SMOOTHING;
      }
      this.anchorWallTime = wallNow;
    }
    this.frames.push(frame);
    if (this.frames.length > SNAPSHOT_BUFFER_SIZE) this.frames.shift();
  }

  /** Wipe — used by `Viewport.setDisconnected` so stale state from a
   *  previous session doesn't ghost-render after reconnect. */
  clear(): void {
    this.frames.length = 0;
    this.clockInitialized = false;
    this.anchorTick = 0;
    this.anchorWallTime = 0;
  }

  /** Latest frame, for non-interpolated reads (HP, walls, yourId). */
  latest(): SnapshotFrame | null {
    return this.frames[this.frames.length - 1] ?? null;
  }

  /** "What server tick are we rendering at" in fractional ticks.
   *  Projects forward from the smoothed clock anchor at the server's
   *  real-time rate, then subtracts the delay so we're always
   *  rendering N ticks in the past.
   *
   *  Unlike the old `latest.tick + (now - latest.arrivedAt) / TICK_MS`
   *  formulation, this DOESN'T jump every time a new snapshot lands —
   *  the anchor moves with the smoothing filter, not with each
   *  arrival, so remote entities ride a continuous tick stream
   *  instead of a per-snapshot stutter. */
  renderTick(nowMs: number): number {
    if (!this.clockInitialized) return 0;
    return (
      this.anchorTick
      + (nowMs - this.anchorWallTime) / SERVER_TICK_RATE_MS
      - SNAPSHOT_DELAY_TICKS
    );
  }

  /** Interpolated entity state at the render tick, written into `out`.
   *  Returns `out` for chaining; `out.present === false` when the
   *  entity isn't in the latest frame (left AOI / disconnected). */
  sample(entityId: number, nowMs: number, out: InterpolatedEntity): InterpolatedEntity {
    out.present = false;
    if (this.frames.length === 0) return out;

    const latest = this.frames[this.frames.length - 1]!;
    const latestIdx = latest.indexById.get(entityId);
    if (latestIdx === undefined) return out;
    out.present = true;
    out.hp = latest.hps[latestIdx]!;

    const target = this.renderTick(nowMs);

    // If we don't have enough history to delay, snap to oldest known.
    // This is the "first ~100 ms after connect" path — we'd rather
    // show the player than show nothing.
    if (this.frames.length === 1 || target < this.frames[0]!.tick) {
      const f = this.frames[0]!;
      const idx = f.indexById.get(entityId) ?? latestIdx;
      const f2 = idx === latestIdx ? latest : f;
      out.x = f2.xs[idx]!;
      out.y = f2.ys[idx]!;
      out.facing = f2.facings[idx]!;
      return out;
    }

    // If the render tick is past the latest snapshot — server is
    // late, jitter spike, etc. — pin to the latest. Brief is explicit
    // about NOT extrapolating (rubber-banding on direction changes
    // looks awful); we hold position instead.
    if (target >= latest.tick) {
      out.x = latest.xs[latestIdx]!;
      out.y = latest.ys[latestIdx]!;
      out.facing = latest.facings[latestIdx]!;
      return out;
    }

    // Walk pairs to find the two snapshots that straddle `target`.
    for (let i = 0; i < this.frames.length - 1; i++) {
      const a = this.frames[i]!;
      const b = this.frames[i + 1]!;
      if (a.tick <= target && target < b.tick) {
        const aIdx = a.indexById.get(entityId);
        const bIdx = b.indexById.get(entityId);
        // If the entity is in only one of the two, fall back to that
        // one's position — no fake motion from a missing endpoint.
        if (aIdx === undefined && bIdx !== undefined) {
          out.x = b.xs[bIdx]!;
          out.y = b.ys[bIdx]!;
          out.facing = b.facings[bIdx]!;
          return out;
        }
        if (bIdx === undefined && aIdx !== undefined) {
          out.x = a.xs[aIdx]!;
          out.y = a.ys[aIdx]!;
          out.facing = a.facings[aIdx]!;
          return out;
        }
        if (aIdx === undefined || bIdx === undefined) {
          // Should be unreachable — `latestIdx` exists so the entity
          // is in *some* frame — but TypeScript needs this.
          out.x = latest.xs[latestIdx]!;
          out.y = latest.ys[latestIdx]!;
          out.facing = latest.facings[latestIdx]!;
          return out;
        }
        const span = b.tick - a.tick;
        const t = span > 0 ? (target - a.tick) / span : 0;
        const ax = a.xs[aIdx]!;
        const ay = a.ys[aIdx]!;
        out.x = ax + (b.xs[bIdx]! - ax) * t;
        out.y = ay + (b.ys[bIdx]! - ay) * t;
        out.facing = lerpAngle(a.facings[aIdx]!, b.facings[bIdx]!, t);
        return out;
      }
    }

    // Fallback (should be unreachable given the bounds checks above).
    out.x = latest.xs[latestIdx]!;
    out.y = latest.ys[latestIdx]!;
    out.facing = latest.facings[latestIdx]!;
    return out;
  }
}

export class Viewport {
  private readonly canvas: HTMLCanvasElement;
  private readonly camera: Camera = makeCamera();
  private readonly size: ViewportSize = { width: 1, height: 1, dpr: 1 };
  private readonly tmpScreen = { x: 0, y: 0 };
  private readonly tmpRect: WorldRect = { minX: 0, minY: 0, maxX: 0, maxY: 0 };

  /** Reused per-frame scratch for `drawWalls`. Three parallel typed
   *  arrays (one per `(wx, wy, kind)` field) avoid allocating per RAF —
   *  at 60 fps with ~100 visible cells that was 6000 tuple objects/sec
   *  hitting the GC. `wallCellsLen` tracks how many entries are live;
   *  the arrays grow with the AOI but never shrink. */
  private wallCellsX = new Int32Array(256);
  private wallCellsY = new Int32Array(256);
  private wallCellsKind = new Uint8Array(256);
  private wallCellsLen = 0;
  /** Reused neighbor-lookup map. Cleared each frame instead of being
   *  reallocated. Keys are packed cell coords; values are wall kinds. */
  private readonly wallKindByKey = new Map<number, number>();

  /** Ring buffer of recent snapshots. Drives the tick-based
   *  interpolation for non-self entities — see `SnapshotBuffer`. */
  private readonly buffer = new SnapshotBuffer();
  /** Reused per-frame scratch for `SnapshotBuffer.sample()` so the
   *  draw loop doesn't allocate per entity per frame. */
  private readonly tmpEntity: InterpolatedEntity = {
    present: false,
    x: 0,
    y: 0,
    facing: 0,
    hp: 0,
  };

  /** Predicted self position resolver, set by main.ts. Returns `null`
   *  before the first input lands (camera then stays at the world
   *  origin and self renders at server position from the snapshot). */
  private selfResolver: SelfPositionResolver | null = null;

  /** Per-frame hook invoked at the very top of `draw()`, before the
   *  predicted self is read. main.ts wires this to its fixed-timestep
   *  accumulator: the SAME vsync clock that paints the frame also drains
   *  the sim steps and computes the interpolation alpha. One clock — no
   *  setTimeout-vs-vsync beat (the micro-jitter). */
  private frameHook: ((nowMs: number) => void) | null = null;

  /** Optional HUD debug string resolver — see DebugResolver. */
  private debugResolver: DebugResolver | null = null;

  /** Live aim angle for self, set by main.ts. Returns `null` before
   *  the input pump is wired; the renderer then falls back to the
   *  snapshot's `facings` for self. */
  private aimResolver: SelfAimResolver | null = null;

  /** Pending wall overlay from `BuildPrediction`, set by main.ts. Pulled
   *  every render frame so the merged wall set is fresh. `null` (or
   *  empty additions + removals) means "no predictions in flight" —
   *  the renderer draws the server's walls untouched. */
  private buildOverlayResolver: BuildOverlayResolver | null = null;

  /** Build-mode cursor state resolver. Pulled every RAF frame so the
   *  placement preview tracks the mouse at native rate. */
  private buildCursorResolver: BuildCursorResolver | null = null;

  private status: ConnectionStatus = { connected: false, lastReason: "boot" };
  private resizeObserver: ResizeObserver | null = null;

  /** FPS measurement state. Counts RAF frames over rolling windows of
   *  ~500 ms — short enough to be responsive to refresh-rate drops
   *  (e.g. browser throttling on a backgrounded tab) but long enough
   *  not to swing wildly on every frame. */
  private fpsWindowStartMs = 0;
  private fpsWindowFrames = 0;
  private currentFps = 0;

  // ---------------------------------------------------------------
  // Pixi scene graph. All nullable until `initPixi` resolves; the
  // ticker is only added after that, so per-frame code never sees a
  // null reference.
  // ---------------------------------------------------------------

  /** The Pixi Application that owns the GL/GPU context. Created sync
   *  in `initPixi` then init()'d asynchronously. */
  private app: Application | null = null;
  /** World-space container, scaled by (ppwu, -ppwu) every frame so its
   *  children draw in WORLD units with y-up. The GPU then composites
   *  this container's transform smoothly across RAF frames — that's
   *  the thing Canvas2D couldn't do without per-shape AA shimmer. */
  private worldContainer: Container | null = null;

  // Per-layer Graphics. Each is cleared + rebuilt every frame; that's
  // cheap on GPU because Pixi batches the draw calls and we only pay
  // for the actual vertex re-upload, not a full re-rasterization.
  private gridGfx: Graphics | null = null;
  private originGfx: Graphics | null = null;
  private wallsGfx: Graphics | null = null;
  private entitiesGfx: Graphics | null = null;
  private buildOverlayGfx: Graphics | null = null;
  /** HP bars live in the HUD container so they stay screen-pixel-sized.
   *  Positions are computed per-frame from each entity's world position
   *  via `worldToScreen` (cheap — runs only for entities with HP<100). */
  private hpBarsGfx: Graphics | null = null;
  /** Top-left HUD readout: tick / entity count / render lag. */
  private hudText: Text | null = null;
  /** Centered placeholder shown when there's no snapshot yet
   *  (connecting, waiting, or disconnected). */
  private placeholderText: Text | null = null;

  /** Resolves once Pixi has finished its async init. `start()` waits
   *  on this before adding the ticker. State-setter methods
   *  (`setSnapshot`, `setSelfResolver`, etc.) don't wait — they just
   *  store, and the first paint happens after init resolves. */
  private readonly readyPromise: Promise<void>;

  /** Set after `start()` so a stop()/start() pair doesn't double-add
   *  the ticker callback. */
  private tickerRunning = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.applyCanvasSize();
    this.readyPromise = this.initPixi();
  }

  /** Boot the Pixi Application. Async because Pixi v8 picks WebGPU
   *  when available (the WebGPU adapter request is async) and falls
   *  back to WebGL otherwise. The returned promise is awaited by
   *  `start()`; before it resolves, anything queued via setters just
   *  buffers and the canvas shows nothing (Pixi hasn't taken it over
   *  yet). */
  private async initPixi(): Promise<void> {
    const app = new Application();
    await app.init({
      canvas: this.canvas,
      width: this.size.width,
      height: this.size.height,
      resolution: this.size.dpr,
      autoDensity: true,
      backgroundColor: BG_COLOR,
      antialias: true,
      // We drive the ticker ourselves in start() so we control exactly
      // when paint begins (after all setters have wired their state).
      autoStart: false,
    });
    this.app = app;

    // World container. Scale + position are updated every frame in
    // `draw()` so the camera transform stays a single GPU matrix.
    const world = new Container();
    app.stage.addChild(world);
    this.worldContainer = world;

    // Layer order (back to front): grid → origin → walls → build
    // overlay → entities. Matches the previous Canvas2D draw order
    // so player + aim ray always read on top of the cell underneath.
    this.gridGfx = new Graphics();
    world.addChild(this.gridGfx);
    this.originGfx = new Graphics();
    world.addChild(this.originGfx);
    this.wallsGfx = new Graphics();
    world.addChild(this.wallsGfx);
    this.buildOverlayGfx = new Graphics();
    world.addChild(this.buildOverlayGfx);
    this.entitiesGfx = new Graphics();
    world.addChild(this.entitiesGfx);

    // HUD container at identity transform (added after the world
    // container so it always paints on top). Local — Pixi holds the
    // strong ref via the stage→hud→children chain; we never need to
    // reach for the container directly after wiring its children.
    const hud = new Container();
    app.stage.addChild(hud);

    this.hpBarsGfx = new Graphics();
    hud.addChild(this.hpBarsGfx);

    this.hudText = new Text({
      text: "",
      style: {
        fontFamily: "ui-monospace, Menlo, monospace",
        fontSize: 11,
        fill: 0xffffff,
      },
    });
    this.hudText.alpha = 0.5;
    this.hudText.position.set(8, 8);
    hud.addChild(this.hudText);

    this.placeholderText = new Text({
      text: "",
      style: {
        fontFamily: "ui-monospace, Menlo, monospace",
        fontSize: 14,
        fill: 0xdcdcdc,
        align: "center",
      },
    });
    this.placeholderText.anchor.set(0.5);
    this.placeholderText.visible = false;
    hud.addChild(this.placeholderText);
  }

  /** Push a new snapshot. The previous one slides into the buffer's
   *  ring for tick-based interpolation. */
  setSnapshot(snap: SnapshotMsg): void {
    const indexById = new Map<number, number>();
    for (let i = 0; i < snap.count; i++) {
      indexById.set(snap.ids[i]!, i);
    }
    const wallKindByKey = new Map<number, number>();
    for (let i = 0; i < snap.wallsCount; i++) {
      wallKindByKey.set(
        packCellKey(snap.wallXs[i]!, snap.wallYs[i]!),
        snap.wallKinds[i]!,
      );
    }
    this.buffer.push({
      tick: snap.tick,
      yourId: snap.yourId,
      arrivedAt: performance.now(),
      ids: snap.ids,
      xs: snap.xs,
      ys: snap.ys,
      facings: snap.facings,
      hps: snap.hps,
      indexById,
      wallXs: snap.wallXs,
      wallYs: snap.wallYs,
      wallKinds: snap.wallKinds,
      wallKindByKey,
    });
  }

  setSelfResolver(resolver: SelfPositionResolver): void {
    this.selfResolver = resolver;
  }

  setFrameHook(cb: (nowMs: number) => void): void {
    this.frameHook = cb;
  }

  setDebugResolver(resolver: DebugResolver): void {
    this.debugResolver = resolver;
  }

  setAimResolver(resolver: SelfAimResolver): void {
    this.aimResolver = resolver;
  }

  setBuildOverlayResolver(resolver: BuildOverlayResolver): void {
    this.buildOverlayResolver = resolver;
  }

  setBuildCursorResolver(resolver: BuildCursorResolver): void {
    this.buildCursorResolver = resolver;
  }

  /** Translate canvas-relative mouse coords → world coords. Used by
   *  the input pump to attach a cursor to outgoing build inputs. */
  cursorWorldFromMouse(mouseCssX: number, mouseCssY: number): {
    x: number;
    y: number;
  } {
    const out = { x: 0, y: 0 };
    screenToWorld(this.camera, this.size, mouseCssX, mouseCssY, out);
    return out;
  }

  setConnected(): void {
    this.status = { connected: true, lastReason: "" };
  }

  setDisconnected(reason: string): void {
    this.status = { connected: false, lastReason: reason };
    // Drop interp state — stale snapshots from a previous session
    // would ghost-render after reconnect.
    this.buffer.clear();
  }

  /** Compute a world-space aim angle from a canvas-relative mouse
   *  position. Used by the input pump (step 14). */
  aimAngleFromMouse(mouseCssX: number, mouseCssY: number): number {
    screenToWorld(this.camera, this.size, mouseCssX, mouseCssY, this.tmpScreen);
    return Math.atan2(
      this.tmpScreen.y - this.camera.centerY,
      this.tmpScreen.x - this.camera.centerX,
    );
  }

  start(): void {
    if (this.tickerRunning) return;
    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.canvas);
    // Wait for Pixi init to resolve, then arm the ticker. If the
    // promise rejects (rare — WebGL context failure), the renderer
    // simply doesn't start; main.ts will still see the snapshot
    // pipeline working, just no visuals.
    void this.readyPromise.then(() => {
      if (this.app === null) return;
      this.tickerRunning = true;
      this.app.ticker.add(this.tickerCallback);
      this.app.start();
    });
  }

  stop(): void {
    if (this.app !== null && this.tickerRunning) {
      this.app.ticker.remove(this.tickerCallback);
      this.app.stop();
      this.tickerRunning = false;
    }
    if (this.resizeObserver !== null) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /** Most recent FPS measurement, rolled over a ~500 ms window. */
  getFps(): number {
    return this.currentFps;
  }

  // ---------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------

  private applyCanvasSize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const cssW = Math.max(1, Math.floor(rect.width));
    const cssH = Math.max(1, Math.floor(rect.height));
    this.size.width = cssW;
    this.size.height = cssH;
    this.size.dpr = dpr;
  }

  private onResize(): void {
    this.applyCanvasSize();
    if (this.app !== null) {
      this.app.renderer.resize(this.size.width, this.size.height);
    }
  }

  /** Pixi ticker callback. We ignore the supplied Ticker (delta time
   *  etc.) and use `performance.now()` directly so the timestamps
   *  agree with the prediction engine's lerp clock and the snapshot
   *  buffer's smoothed anchor. */
  private tickerCallback = (_ticker: Ticker): void => {
    this.draw();
  };

  private draw(): void {
    if (
      this.app === null ||
      this.worldContainer === null ||
      this.gridGfx === null ||
      this.originGfx === null ||
      this.wallsGfx === null ||
      this.entitiesGfx === null ||
      this.buildOverlayGfx === null ||
      this.hpBarsGfx === null ||
      this.hudText === null ||
      this.placeholderText === null
    ) {
      return;
    }

    const nowMs = performance.now();

    // Drive the fixed-timestep sim accumulator BEFORE reading the
    // predicted self below — this drains any due input `step()`s and
    // updates the interpolation alpha off this same vsync clock.
    this.frameHook?.(nowMs);

    // FPS measurement: count frames over a ~500 ms rolling window.
    if (this.fpsWindowStartMs === 0) {
      this.fpsWindowStartMs = nowMs;
    } else {
      this.fpsWindowFrames++;
      const elapsed = nowMs - this.fpsWindowStartMs;
      if (elapsed >= 500) {
        this.currentFps = (this.fpsWindowFrames * 1000) / elapsed;
        this.fpsWindowStartMs = nowMs;
        this.fpsWindowFrames = 0;
      }
    }

    // Camera follow — predicted self position resolver returns null
    // before the first input lands; until then the camera stays at
    // whatever was last set (origin on boot).
    const selfPos = this.selfResolver?.() ?? null;
    if (selfPos !== null) {
      this.camera.centerX = selfPos.x;
      this.camera.centerY = selfPos.y;
    }

    // Apply the camera transform to the world container in ONE place.
    // `scale.set(ppwu, -ppwu)` flips y and zooms. `position.set(...)`
    // shifts the world so that world (camera.centerX, camera.centerY)
    // lands at the screen center. All world-space drawing below uses
    // raw world coords; Pixi composites this transform on the GPU
    // every frame at vsync, producing smooth sub-pixel scrolling
    // without the per-shape AA shimmer that Canvas2D suffered.
    const ppwu = this.camera.pixelsPerWorldUnit;
    this.worldContainer.scale.set(ppwu, -ppwu);
    this.worldContainer.position.set(
      this.size.width * 0.5 - this.camera.centerX * ppwu,
      this.size.height * 0.5 + this.camera.centerY * ppwu,
    );

    const latest = this.buffer.latest();
    if (latest === null) {
      // No snapshot yet — wipe world layers, show centered placeholder.
      this.gridGfx.clear();
      this.originGfx.clear();
      this.wallsGfx.clear();
      this.entitiesGfx.clear();
      this.buildOverlayGfx.clear();
      this.hpBarsGfx.clear();
      this.hudText.text = "";
      this.placeholderText.text = this.status.connected
        ? "connected — waiting for snapshot…"
        : `disconnected: ${this.status.lastReason}`;
      this.placeholderText.position.set(
        this.size.width * 0.5,
        this.size.height * 0.5,
      );
      this.placeholderText.visible = true;
      return;
    }
    this.placeholderText.visible = false;

    // Pull the live build cursor state once per frame.
    const cursor = this.buildCursorResolver?.() ?? null;

    this.drawGrid();
    this.drawOrigin();
    this.drawWalls(latest);
    this.drawEntities(latest, selfPos, nowMs);
    if (cursor !== null && cursor.active && selfPos !== null) {
      this.drawBuildOverlay(selfPos, cursor);
    } else {
      this.buildOverlayGfx.clear();
    }
  }

  private drawGrid(): void {
    const g = this.gridGfx!;
    g.clear();
    const cam = this.camera;
    const ppwu = cam.pixelsPerWorldUnit;
    if (ppwu < 4) return;

    const rect = visibleWorldRect(cam, this.size, this.tmpRect);
    const minXi = Math.floor(rect.minX);
    const maxXi = Math.ceil(rect.maxX);
    const minYi = Math.floor(rect.minY);
    const maxYi = Math.ceil(rect.maxY);

    // Strokes inside the y-flipped, zoomed container: a "1 screen
    // pixel" line is `1 / ppwu` world units wide. Pixi GPU-rasterizes
    // it as a true ~1 device pixel hairline regardless of zoom.
    const px = 1 / ppwu;

    // Minor lines — every cell that's not a major.
    for (let wx = minXi; wx <= maxXi; wx++) {
      if (wx % 10 === 0) continue;
      g.moveTo(wx, rect.minY);
      g.lineTo(wx, rect.maxY);
    }
    for (let wy = minYi; wy <= maxYi; wy++) {
      if (wy % 10 === 0) continue;
      g.moveTo(rect.minX, wy);
      g.lineTo(rect.maxX, wy);
    }
    g.stroke({ color: 0xffffff, alpha: 0.04, width: GRID_MINOR_PX * px });

    // Major lines — every 10th cell.
    for (let wx = minXi; wx <= maxXi; wx++) {
      if (wx % 10 !== 0) continue;
      g.moveTo(wx, rect.minY);
      g.lineTo(wx, rect.maxY);
    }
    for (let wy = minYi; wy <= maxYi; wy++) {
      if (wy % 10 !== 0) continue;
      g.moveTo(rect.minX, wy);
      g.lineTo(rect.maxX, wy);
    }
    g.stroke({ color: 0xffffff, alpha: 0.10, width: GRID_MAJOR_PX * px });
  }

  private drawOrigin(): void {
    const g = this.originGfx!;
    g.clear();
    const ppwu = this.camera.pixelsPerWorldUnit;
    // ~8 px arms in world units so the crosshair is the same physical
    // size regardless of zoom.
    const extent = 8 / ppwu;
    const px = 1 / ppwu;
    g.moveTo(-extent, 0);
    g.lineTo(extent, 0);
    g.moveTo(0, -extent);
    g.lineTo(0, extent);
    g.stroke({ color: 0xff5050, alpha: 0.6, width: ORIGIN_PX * px });
  }

  private drawWalls(latest: SnapshotFrame): void {
    const g = this.wallsGfx!;
    g.clear();
    const ppwu = this.camera.pixelsPerWorldUnit;
    // Half-pixel fill bleed so adjacent same-kind cells overlap and don't
    // leave an AA seam — see WALL_FILL_BLEED_PX.
    const bleed = WALL_FILL_BLEED_PX / ppwu;

    // Merge server walls with the client-side build prediction.
    const overlay = this.buildOverlayResolver?.() ?? null;
    const removals = overlay?.removals ?? EMPTY_REMOVALS;
    const additions = overlay?.additions ?? EMPTY_ADDITIONS;

    // Build the merged cell list + (key → kind) map into the
    // preallocated scratch buffers — see field doc.
    this.wallKindByKey.clear();
    this.wallCellsLen = 0;
    const totalCellCap =
      latest.wallXs.length + (additions as ReadonlyArray<unknown>).length;
    if (this.wallCellsX.length < totalCellCap) {
      const newCap = Math.max(totalCellCap, this.wallCellsX.length * 2);
      this.wallCellsX = new Int32Array(newCap);
      this.wallCellsY = new Int32Array(newCap);
      this.wallCellsKind = new Uint8Array(newCap);
    }
    for (let i = 0; i < latest.wallXs.length; i++) {
      const wx = latest.wallXs[i]!;
      const wy = latest.wallYs[i]!;
      const key = packCellKey(wx, wy);
      if (removals.has(key)) continue;
      const kind = latest.wallKinds[i] ?? WALL_KIND_PLAYER;
      this.wallKindByKey.set(key, kind);
      const j = this.wallCellsLen++;
      this.wallCellsX[j] = wx;
      this.wallCellsY[j] = wy;
      this.wallCellsKind[j] = kind;
    }
    for (const [wx, wy] of additions) {
      const key = packCellKey(wx, wy);
      if (this.wallKindByKey.has(key)) continue;
      this.wallKindByKey.set(key, PREDICTED_WALL_KIND);
      const j = this.wallCellsLen++;
      this.wallCellsX[j] = wx;
      this.wallCellsY[j] = wy;
      this.wallCellsKind[j] = PREDICTED_WALL_KIND;
    }
    const cellsLen = this.wallCellsLen;
    const cellsX = this.wallCellsX;
    const cellsY = this.wallCellsY;
    const cellsKind = this.wallCellsKind;
    const wallKindByKey = this.wallKindByKey;

    // Fill pass: one Graphics path per kind. Adjacent same-kind rects
    // inside one path rasterize as ONE shape on the GPU — no internal
    // seam between cells of the same group. Same merging technique
    // we used in Canvas2D with Path2D, just GPU-accelerated.
    for (let p = 0; p < 2; p++) {
      const kindPass = p === 0 ? WALL_KIND_PLAYER : WALL_KIND_BARRIER;
      for (let i = 0; i < cellsLen; i++) {
        if (cellsKind[i] !== kindPass) continue;
        // Cell (wx, wy) spans [wx, wx+1] × [wy, wy+1] in world coords.
        // The container's negative y-scale flips it to the right
        // screen orientation automatically — we don't have to do the
        // (wy + 1) anchor dance we did for Canvas2D. The bleed grows the
        // rect outward so neighbors overlap and no AA seam shows.
        g.rect(cellsX[i]! - bleed, cellsY[i]! - bleed, 1 + 2 * bleed, 1 + 2 * bleed);
      }
      g.fill(kindPass === WALL_KIND_PLAYER ? WALL_FILL_PLAYER : WALL_FILL_BARRIER);
    }

    // Outline pass: only edges that face a non-wall OR a different
    // kind of wall. Same-kind internal edges are skipped, so a
    // connected group reads as one outlined shape. Two stroke
    // passes (one per kind) so each side of a player↔barrier seam
    // carries its own outline color.
    if (ppwu < 8) return; // outlines are noise at low zoom
    const px = 1 / ppwu;
    for (let p = 0; p < 2; p++) {
      const kindPass = p === 0 ? WALL_KIND_PLAYER : WALL_KIND_BARRIER;
      for (let i = 0; i < cellsLen; i++) {
        if (cellsKind[i] !== kindPass) continue;
        const wx = cellsX[i]!;
        const wy = cellsY[i]!;
        // World y-up: world neighbor at (wx, wy+1) is the cell ABOVE
        // this one. Mapping (post-flip in the container):
        //   world TOP edge    (wy+1) ←→ neighbor (wx, wy+1)
        //   world BOTTOM edge (wy)   ←→ neighbor (wx, wy-1)
        //   world LEFT edge   (wx)   ←→ neighbor (wx-1, wy)
        //   world RIGHT edge  (wx+1) ←→ neighbor (wx+1, wy)
        if (wallKindByKey.get(packCellKey(wx, wy + 1)) !== kindPass) {
          g.moveTo(wx, wy + 1);
          g.lineTo(wx + 1, wy + 1);
        }
        if (wallKindByKey.get(packCellKey(wx, wy - 1)) !== kindPass) {
          g.moveTo(wx, wy);
          g.lineTo(wx + 1, wy);
        }
        if (wallKindByKey.get(packCellKey(wx - 1, wy)) !== kindPass) {
          g.moveTo(wx, wy);
          g.lineTo(wx, wy + 1);
        }
        if (wallKindByKey.get(packCellKey(wx + 1, wy)) !== kindPass) {
          g.moveTo(wx + 1, wy);
          g.lineTo(wx + 1, wy + 1);
        }
      }
      g.stroke({
        color:
          kindPass === WALL_KIND_PLAYER
            ? WALL_STROKE_PLAYER
            : WALL_STROKE_BARRIER,
        width: WALL_OUTLINE_PX * px,
      });
    }
  }

  private drawEntities(
    latest: SnapshotFrame,
    selfPos: { x: number; y: number } | null,
    nowMs: number,
  ): void {
    const g = this.entitiesGfx!;
    const hp = this.hpBarsGfx!;
    g.clear();
    hp.clear();
    const cam = this.camera;
    const ppwu = cam.pixelsPerWorldUnit;
    const px = 1 / ppwu;
    const renderTick = this.buffer.renderTick(nowMs);

    for (let i = 0; i < latest.ids.length; i++) {
      const id = latest.ids[i]!;
      let wx: number;
      let wy: number;
      let facing: number;
      let hpVal: number;

      if (id === latest.yourId && selfPos !== null) {
        // Self: predicted position + live aim. Facing falls back to
        // the snapshot's value before the aim resolver is wired.
        wx = selfPos.x;
        wy = selfPos.y;
        facing = this.aimResolver?.() ?? latest.facings[i]!;
        hpVal = latest.hps[i]!;
      } else {
        // Non-self: tick-based interpolation through the snapshot
        // buffer.
        const e = this.buffer.sample(id, nowMs, this.tmpEntity);
        if (!e.present) continue;
        wx = e.x;
        wy = e.y;
        facing = e.facing;
        hpVal = e.hp;
      }

      // Body — circle at the entity's world position. Pixi handles
      // sub-pixel scroll smoothly so the circle slides as the camera
      // pans, no AA shimmer.
      g.circle(wx, wy, PLAYER_RADIUS_WORLD);
      g.fill(hueForId(id));
      if (id === latest.yourId) {
        // Self outline so it's findable in a crowd.
        g.circle(wx, wy, PLAYER_RADIUS_WORLD);
        g.stroke({ color: 0xffffff, alpha: 0.85, width: 2 * px });
      }

      // Diagnostic hit + crit rings — temporary visualization of the
      // server's damage geometry.
      if (DRAW_HITBOX_OVERLAYS) {
        g.circle(wx, wy, HIT_RADIUS_WORLD);
        g.stroke({ color: HIT_RING_COLOR, width: 1.5 * px });
        g.circle(wx, wy, CRIT_RADIUS_WORLD);
        g.stroke({ color: CRIT_RING_COLOR, width: 1.5 * px });
      }

      // Aim ray. In the y-flipped container, world y goes up, so we
      // don't need to negate `Math.sin(facing)` like Canvas2D did.
      const aimLenWorld = PLAYER_RADIUS_WORLD + 0.3;
      g.moveTo(wx, wy);
      g.lineTo(
        wx + Math.cos(facing) * aimLenWorld,
        wy + Math.sin(facing) * aimLenWorld,
      );
      g.stroke({ color: 0xffffff, alpha: 0.7, width: 2 * px });

      // HP bar — drawn in the HUD container (screen space) so it
      // stays a constant pixel size regardless of camera zoom. We
      // compute the player's screen position once and place the bar
      // above the player circle.
      if (hpVal < 100) {
        worldToScreen(cam, this.size, wx, wy, this.tmpScreen);
        const r = PLAYER_RADIUS_WORLD * ppwu;
        const barW = r * 2.2;
        const barH = 3;
        const barX = this.tmpScreen.x - barW * 0.5;
        const barY = this.tmpScreen.y - r - 8;
        hp.rect(barX, barY, barW, barH);
        hp.fill({ color: 0x000000, alpha: 0.6 });
        hp.rect(barX, barY, (barW * hpVal) / 100, barH);
        hp.fill(hpVal > 30 ? 0x5ec25e : 0xd54a4a);
      }
    }

    // HUD lag readout. Show how far behind the latest snapshot we're
    // rendering — a steady ~3 ticks means smooth playback; spikes
    // mean network jitter.
    const lag = (latest.tick - renderTick).toFixed(1);
    const dbg = this.debugResolver?.() ?? null;
    this.hudText!.text =
      `tick ${latest.tick}  entities ${latest.ids.length}  render lag ${lag}t`
      + (dbg !== null ? `  ${dbg}` : "");
  }

  private drawBuildOverlay(
    selfPos: { x: number; y: number },
    cursor: BuildCursorState,
  ): void {
    const g = this.buildOverlayGfx!;
    g.clear();
    const ppwu = this.camera.pixelsPerWorldUnit;
    const px = 1 / ppwu;

    // Faint outer ring showing the build radius. Always visible while
    // in build mode so the player has a reach reference.
    g.circle(selfPos.x, selfPos.y, BUILD_RADIUS_WORLD);
    g.stroke({ color: 0xffffff, alpha: 0.10, width: px });

    // Mouse → world.
    const mouseWorld = { x: 0, y: 0 };
    screenToWorld(
      this.camera,
      this.size,
      cursor.mouseCanvasX,
      cursor.mouseCanvasY,
      mouseWorld,
    );

    if (cursor.breakHeld) {
      // BREAK mode: filled + stroked AoE circle at cursor.
      g.circle(mouseWorld.x, mouseWorld.y, BREAK_AOE_RADIUS_WORLD);
      g.fill(BREAK_FILL);
      g.circle(mouseWorld.x, mouseWorld.y, BREAK_AOE_RADIUS_WORLD);
      g.stroke({ color: BREAK_STROKE, width: 1.5 * px });
    } else {
      // PLACE preview: white cell outline at the cell under cursor.
      // Thicker stroke + brighter alpha when in range so the player
      // knows the click won't no-op.
      const targetCellX = Math.floor(mouseWorld.x);
      const targetCellY = Math.floor(mouseWorld.y);
      const centerWorldX = targetCellX + 0.5;
      const centerWorldY = targetCellY + 0.5;
      const inRange =
        (centerWorldX - selfPos.x) ** 2 + (centerWorldY - selfPos.y) ** 2
          <= BUILD_RADIUS_WORLD * BUILD_RADIUS_WORLD;
      g.rect(targetCellX, targetCellY, 1, 1);
      g.stroke({
        color: inRange ? BUILD_OUTLINE_COLOR : BUILD_OUTLINE_OUT_OF_RANGE_COLOR,
        width: (inRange ? 2 : 1) * px,
      });
    }
  }
}

/** Deterministic id → HSL hue string. Golden-angle multiplication gives
 *  an even spread without a lookup table. Pixi v8's color subsystem
 *  parses CSS color strings directly, so we keep the HSL form for
 *  readability rather than pre-converting to a numeric color. */
function hueForId(id: number): string {
  const hue = (id * 137) % 360;
  return `hsl(${hue}, 70%, 55%)`;
}
